/* eslint-disable sonarjs/no-duplicate-string */

import {
  takeEvery, put, takeLatest, call,
} from 'redux-saga/effects';

import { MODALS, API_URLS, FETCH_OPTIONS } from '../constants';
import { requestGetDownloadedFile, requestPost } from '../utils/request';
import { aExportTennisToExcelRequest, aExportToExcelRequest, aGetOddsDetails, aGetTennisOddsDetails } from '../reducers';
import { aSetIsFetching, aSetOpenModal } from '../reducers/common';
import { aExportTennisToExcellSuccess, aExportToExcellSuccess, aGetOddsDetailsSuccess, aGetTennisOddsDetailsSuccess } from '../reducers/oddsHistory';

/**
 * @namespace sagas/oddsHistory
 */
/**
 * @memberof sagas/oddsHistory
 */
/**
 * @memberof sagas/oddsHistory
 * @typedef getOddsHistory/response.data
 * @type {object}
 */
/**
 * @memberof sagas/oddsHistory
 * @async
 * @param {object} action
 */
function* getOddsHistory(action) {
  let response;
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_ODDS_HISTORY,
      value: true,
    }));

    response = yield call(requestPost, API_URLS.GET_ODDS_DETAILS,
      {
        matchId: action.payload.matchId,
        markets: action.payload.selectedMarkets,
      },
      true);

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      yield put(aGetOddsDetailsSuccess({
        oddsDetails: response.data.oddsDetails,
        marketsDisplayed: response.data.marketsDisplayed,
      }));
    }
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_ODDS_HISTORY,
      value: false,
    }));
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.GET_ODDS_HISTORY,
      value: false,
    }));
  }
}

/**
 * @param {object} action
 */
function* getTennisOddsHistory(action) {
  let response;
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_TENNIS_ODDS_HISTORY,
      value: true,
    }));

    response = yield call(requestPost, API_URLS.GET_TENNIS_ODDS_DETAILS,
      {
        matchId: action.payload.matchId,
        markets: action.payload.selectedMarkets,
      },
      true);

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      yield put(aGetTennisOddsDetailsSuccess({
        oddsDetails: response.data.oddsDetails,
        marketsDisplayed: response.data.marketsDisplayed,
      }));
    }
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_TENNIS_ODDS_HISTORY,
      value: false,
    }));
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.GET_TENNIS_ODDS_HISTORY,
      value: false,
    }));
  }
}

/**
 * download excel file
 *
 * @memberof sagas/oddsHistory
 * @async
 * @param {object} action
 */
function* getExcelFile(action) {
  let response;
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_EXCEL_FILE,
      value: true,
    }));
    const markets = action.payload.markets.map((el) => `markets[]=${el}`).join('&');
    response = yield call(
      requestGetDownloadedFile, `${API_URLS.DOWNLOAD_EXCEL_FILE}?matchId=${action.payload.matchId}&${markets}`, true,
    );
    if (response) {
      yield put(aExportToExcellSuccess({
        downloadedExcelFile: response,
      }));
      yield put(aSetIsFetching({
        key: FETCH_OPTIONS.GET_EXCEL_FILE,
        value: false,
      }));
    } else {
      const e = new Error(response.error.message);
      e.modal = MODALS.ERROR;
      throw e;
    }
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.GET_EXCEL_FILE,
      value: false,
    }));
  }
}

/**
 * download tennis excel file
 *
 * @memberof sagas/oddsHistory
 * @async
 * @param {object} action
 */
function* getTennisExcelFile(action) {
  let response;
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_TENNIS_EXCEL_FILE,
      value: true,
    }));
    const markets = action.payload.markets.map((el) => `markets[]=${el}`).join('&');
    response = yield call(
      requestGetDownloadedFile,
      `${API_URLS.DOWNLOAD_TENNIS_EXCEL_FILE}?matchId=${action.payload.matchId}&${markets}`, true,
    );

    if (response) {
      yield put(aExportTennisToExcellSuccess({
        downloadedExcelFile: response,
      }));
      yield put(aSetIsFetching({
        key: FETCH_OPTIONS.GET_TENNIS_EXCEL_FILE,
        value: false,
      }));
    } else {
      const e = new Error(response.error.message);
      e.modal = MODALS.ERROR;
      throw e;
    }
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.GET_TENNIS_EXCEL_FILE,
      value: false,
    }));
  }
}

/**
 * @memberof sagas/oddsHistory
 * @async
 * @returns {void}
 */
export default function* oddsHistorySaga() {
  yield takeEvery(aGetOddsDetails, getOddsHistory);
  yield takeEvery(aGetTennisOddsDetails, getTennisOddsHistory);
  yield takeLatest(aExportToExcelRequest, getExcelFile);
  yield takeLatest(aExportTennisToExcelRequest, getTennisExcelFile);
}

